/**
 * Traduction des erreurs d'authentification en clés i18n.
 *
 * RÈGLE : le tri se fait sur `AuthError.code`, JAMAIS sur `error.message`. Les messages anglais de
 * Supabase changent d'une version de GoTrue à l'autre ; une correspondance construite dessus casse
 * en silence, sans qu'aucun test ne l'attrape. C'est ce qui est arrivé le 13/09/2026 : une panne
 * d'envoi d'email (domaine non vérifié chez Resend) a affiché « Error sending confirmation email »
 * en toutes lettres sous le champ mot de passe.
 *
 * Aucune chaîne n'est produite ici : les fonctions renvoient une CLÉ i18n, ou `null` quand rien ne
 * doit s'afficher. Les écrans rendent la clé via `authErrorText`.
 *
 * Trois familles, et elles sont exhaustives — tout code qui n'est pas dans les tables ci-dessous
 * tombe en famille 2.
 *   1. L'utilisateur peut agir       → le message dit quoi faire.
 *   2. L'utilisateur ne peut rien    → le message dit que ça vient de nous, et ouvre la seule
 *                                      porte qui reste (réessayer, ou un autre moyen de connexion).
 *   3. Rien à afficher               → annulation volontaire, ce n'est pas une erreur.
 */
import { Platform } from 'react-native';
import { isAuthRetryableFetchError, isAuthSessionMissingError } from '@supabase/supabase-js';
import { capturerIncident } from '../services/sentry';
import { t } from '../i18n';

export type AuthContext = 'signup' | 'signin' | 'oauth' | 'signout' | 'confirm' | 'reset';

/**
 * ADRESSE DÉJÀ UTILISÉE — VOLONTAIREMENT ABSENTE DES TABLES. NE PAS « CORRIGER ».
 *
 * `signUpEmail` renvoie pour ces deux codes le MÊME retour qu'une inscription réussie (le message
 * neutre `auth.pending`), et non un libellé dédié. C'est de l'anti-énumération : un message qui
 * dirait « cette adresse est déjà prise » confirmerait à n'importe qui l'existence d'un compte.
 * Le choix est ancien et documenté dans `useAuth.ts` (interface `AuthResult`).
 *
 * Avec la confirmation par email activée, Supabase ne renvoie de toute façon pas ces codes : il
 * répond un utilisateur factice sans session. Un libellé dédié serait donc dormant aujourd'hui —
 * et se réveillerait en fuite le jour où quelqu'un désactive la confirmation.
 */
export const ENUMERATION_CODES = new Set(['email_exists', 'user_already_exists']);

// FAMILLE 1 — l'utilisateur peut agir, et le conseil ne dépend pas de l'endroit où il est.
// Tous ces codes appartiennent au type `ErrorCode` de @supabase/auth-js 2.107.0 (vérifié).
const ACTIONABLE: Record<string, string> = {
  invalid_credentials: 'auth.error.invalidCredentials',
  email_not_confirmed: 'auth.error.emailNotConfirmed',
  email_address_invalid: 'auth.error.emailInvalid',
  validation_failed: 'auth.error.validationFailed',
  weak_password: 'auth.error.weakPassword',
  over_email_send_rate_limit: 'auth.error.rateLimit',
  over_request_rate_limit: 'auth.error.rateLimit',
  user_banned: 'auth.error.userBanned',
  // Nouveau mot de passe identique à l'ancien : seule la personne peut y remédier.
  same_password: 'auth.error.samePassword',
};

// FAMILLE 1 (suite) — mêmes codes, conseil DIFFÉRENT selon le contexte. `bad_code_verifier` sur un
// lien de confirmation signifie « ouvert sur un autre appareil » (le vérifieur PKCE vit dans
// l'AsyncStorage du téléphone qui a lancé l'inscription) ; le même code au retour d'un OAuth Google
// ne veut rien dire pour l'utilisateur et retombe donc en famille 2.
const BY_CONTEXT: Partial<Record<AuthContext, Record<string, string>>> = {
  confirm: {
    otp_expired: 'auth.error.confirmExpired',
    flow_state_expired: 'auth.error.confirmExpired',
    bad_code_verifier: 'auth.error.confirmWrongDevice',
    flow_state_not_found: 'auth.error.confirmWrongDevice',
  },
  // Réinitialisation du mot de passe (demande, lien de retour, enregistrement). Même logique que
  // `confirm` : le vérifieur PKCE vit sur le téléphone qui a demandé le lien.
  reset: {
    otp_expired: 'auth.error.resetExpired',
    flow_state_expired: 'auth.error.resetExpired',
    bad_code_verifier: 'auth.error.resetWrongDevice',
    flow_state_not_found: 'auth.error.resetWrongDevice',
    // La session de récupération a disparu ou expiré PENDANT la saisie du nouveau mot de passe :
    // le bon conseil est de redemander un lien, pas « ça vient de nous ».
    session_expired: 'auth.error.resetExpired',
    session_not_found: 'auth.error.resetExpired',
    refresh_token_not_found: 'auth.error.resetExpired',
    refresh_token_already_used: 'auth.error.resetExpired',
  },
  oauth: {
    // Le navigateur a refusé d'ouvrir la fenêtre de connexion Google. Ce code vient
    // d'`expo-web-browser`, pas de Supabase : il est LEVÉ quand `window.open` rend `null`, ce qui
    // arrive lorsque l'appel survient trop longtemps après le geste de l'utilisateur — le cas de
    // Safari, où `signInWithOAuth` insère deux `await` (challenge PKCE, écriture du vérifieur)
    // entre le clic et l'ouverture. L'utilisateur PEUT agir, donc famille 1 : il autorise les
    // fenêtres, ou passe par email. Mais nous aussi devons le savoir — d'où le drapeau ci-dessous.
    ERR_WEB_BROWSER_BLOCKED: 'auth.error.popupBlocked',
    // « The authorization attempt failed for an unknown reason » — lu dans
    // `expo-apple-authentication/ios/AppleAuthenticationExceptions.swift`. Le code est un
    // fourre-tout à TROIS étages : Apple répond `ASAuthorizationError.unknown` ; ou Apple répond
    // un code plus récent que le `switch` du paquet (`@unknown default`) ; ou l'erreur n'est même
    // pas une `ASAuthorizationError` (branche `else` de `didCompleteWithError`). Deux de ces trois
    // cas ne disent pas « Apple ne sait pas » mais « le paquet ne sait pas ».
    //
    // IL A SON PROPRE LIBELLÉ NON PARCE QU'ON CONNAÎT SA CAUSE, MAIS PARCE QUE LE REPLI MENT.
    // `serverOauth` affirme deux choses fausses ici : « le problème vient de nous » — non, notre
    // serveur n'est jamais joint, l'échec est entier dans le flux Apple — et « réessaie dans
    // quelques minutes » — non, réessayer ne change rien si iCloud n'est pas connecté ou si la
    // double authentification manque, qui sont les causes documentées les plus fréquentes. Dire
    // « ça vient de nous » désamorce la seule action utile ; c'est pire que vague.
    ERR_REQUEST_UNKNOWN: 'auth.error.appleUnknown',
  },
};

/**
 * TÉLÉMÉTRIE — LA SECONDE DÉCISION, ET ELLE NE SE DÉDUIT PAS DE LA PREMIÈRE.
 *
 * Avant, la capture Sentry était inférée de « je ne sais pas quoi afficher » : on remontait un
 * incident exactement quand la clé retombait sur `SERVER`. Le raccourci paraît tenir, et il est
 * faux — NOMMER UNE ERREUR NE LA REND PAS BÉNIGNE. Une fenêtre bloquée mérite un message précis
 * ET une remontée : le message parce que l'utilisateur peut la débloquer, la remontée parce que
 * nous seuls pouvons décider d'ouvrir la fenêtre autrement. Avec l'ancienne règle, lui donner un
 * message l'aurait rendue invisible dans Sentry — c'est-à-dire qu'on aurait payé la clarté par
 * l'aveuglement.
 *
 * Cette table vit ICI, contre les deux autres, et pas dans `resolveAuthError` : ajouter un code
 * sans se demander s'il doit remonter doit être impossible à faire par distraction. C'est aussi
 * pourquoi la télémétrie n'est PAS re-décidée ailleurs, dans les appelants : deux endroits qui
 * statuent sur le sort d'une même erreur finissent toujours par diverger.
 *
 * DÉFAUT POUR UN CODE INCONNU : capture. L'absence d'un code de ces tables reste, elle, un signal
 * — c'est ce qui avait alerté le 13/09.
 */
const A_CAPTURER = new Set<string>(['ERR_WEB_BROWSER_BLOCKED', 'ERR_REQUEST_UNKNOWN']);

/** Un code est-il décrit par les tables ? Sert à distinguer « connu » d'« inconnu ». */
function estConnu(code: string | undefined, context: AuthContext): boolean {
  if (!code) return false;
  return BY_CONTEXT[context]?.[code] !== undefined || ACTIONABLE[code] !== undefined;
}

/**
 * Faut-il remonter cet incident ? Décidé par les TABLES, jamais par la clé obtenue.
 *
 * Exportée pour être testable code par code, au même titre qu'`authErrorKey` : ce sont les deux
 * décisions que prend ce module, et elles se vérifient séparément.
 */
export function doitCapturer(code: string | undefined, context: AuthContext): boolean {
  if (!estConnu(code, context)) return true;
  return A_CAPTURER.has(code as string);
}

// FAMILLE 2 — panne ou code inconnu. Un code qu'on ne connaît pas et une panne sont indiscernables
// du point de vue de l'utilisateur : il ne peut rien faire dans les deux cas.
const SERVER: Record<AuthContext, string> = {
  signup: 'auth.error.serverSignup',
  signin: 'auth.error.serverSignin',
  oauth: 'auth.error.serverOauth',
  signout: 'auth.error.serverSignout',
  confirm: 'auth.error.serverConfirm',
  reset: 'auth.error.serverReset',
};

/**
 * LA fonction de correspondance : un code, un contexte, une clé. Pure et sans dépendance à
 * Supabase — c'est elle que les tests parcourent code par code.
 *
 * Un code absent des tables (ou `undefined`) n'est jamais une erreur : il tombe en famille 2.
 */
export function authErrorKey(code: string | undefined, context: AuthContext): string {
  const key = (code && BY_CONTEXT[context]?.[code]) || (code && ACTIONABLE[code]);
  return key || SERVER[context];
}

/**
 * Extrait le code d'une erreur Supabase et le passe à `authErrorKey`.
 *
 * La panne réseau est le seul cas qui ne PEUT pas passer par le code : supabase-js lève un
 * `AuthRetryableFetchError` avec `code: undefined` et `status: 0`. Il se reconnaît par son type,
 * jamais par son message (« Network request failed » est une chaîne de React Native, pas de
 * Supabase).
 */
export function resolveAuthError(error: unknown, context: AuthContext): string {
  if (isAuthRetryableFetchError(error)) return 'auth.error.network';
  // `AuthSessionMissingError` n'a pas de code : il se reconnaît par son type. Dans le contexte de
  // la réinitialisation, c'est la session de récupération qui n'est plus là.
  if (context === 'reset' && isAuthSessionMissingError(error)) return 'auth.error.resetExpired';

  const raw = (error as { code?: unknown } | null | undefined)?.code;
  const code = typeof raw === 'string' ? raw : undefined;
  const key = authErrorKey(code, context);
  // Sans cette trace, une panne serveur devient invisible au lieu d'être moche : c'est le message
  // anglais qui avait alerté le 13/09. On journalise le brut, on ne l'affiche jamais.
  //
  // C'EST ICI, ET NULLE PART AILLEURS, QUE SENTRY EST APPELÉ POUR L'AUTHENTIFICATION. La panne
  // réseau est déjà repartie plus haut : un mode avion n'est pas un incident, et le remonter
  // noierait le signal sous le bruit.
  //
  // LA CONDITION EST `doitCapturer`, PAS `key === SERVER[context]`. L'ancienne forme déduisait la
  // télémétrie de l'affichage, ce qui rendait impossible de nommer une erreur sans la faire
  // disparaître de Sentry. Les deux décisions sont désormais prises séparément, dans les tables.
  if (doitCapturer(code, context)) {
    console.warn('[auth]', context, code ?? 'no_code', error instanceof Error ? error.message : String(error));
    capturerIncident(code ?? 'no_code', context);
  }
  return key;
}

/**
 * FAMILLE 3 — annulation volontaire. Fermer la feuille Apple n'est pas une erreur : on ne montre
 * rien plutôt qu'un message qui reprocherait à quelqu'un d'avoir changé d'avis.
 */
export function isUserCancellation(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { code?: string }).code === 'ERR_REQUEST_CANCELED';
}

/**
 * Erreurs du RETOUR OAuth, lues dans les paramètres d'URL. Ce vocabulaire est celui de la RFC 6749
 * (`access_denied`, `server_error`…), pas celui de Supabase : ces valeurs n'ont pas leur place dans
 * les tables ci-dessus, et ne doivent jamais être affichées telles quelles.
 *
 * `null` = refus du consentement, c'est-à-dire une annulation (famille 3).
 */
export function oauthReturnErrorKey(error: string): string | null {
  if (error === 'access_denied') return null;
  console.warn('[auth]', 'oauth', 'oauth_return', error);
  return SERVER.oauth;
}

/**
 * Rend une clé en texte. Les libellés de famille 2 proposent un autre moyen de connexion, et
 * lesquels dépendent de la plateforme : le bouton Apple n'existe que sur iOS. L'interpolation est
 * faite ICI et non par les écrans — six appelants qui l'oublieraient afficheraient
 * « [missing %{providers} value] » à la place du conseil.
 */
export function authErrorText(key: string): string {
  const providers = t(Platform.OS === 'ios' ? 'auth.error.providersIos' : 'auth.error.providersAndroid');
  return t(key, { providers });
}
